import dayjs from 'dayjs';
import { CampaignConfigSchema, FormMessage, ScheduleKind } from '../types';
import { CampaignPolicy } from '../policies';
import { newClientKey } from '../helpers/clientKey';
import { getGoverningMessageTemplate } from './messageHelpers';
import { convertHtmlToText } from 'src/helpers/helpers';

/**
 * Pure logic behind the message list. Lifted out of MessagesField so it can be
 * tested without rendering the accordion tree, and so the component is left
 * with layout only.
 */

// Subtitle shown under a collapsed message accordion.
export function getScheduleLabel(message: FormMessage, scheduleKind: ScheduleKind, index: number): string {
  const { scheduledType, delay, time } = message;
  if (scheduledType === 'contact_added') {
    return 'Scheduled: When contact is added to an appointment';
  }
  if (scheduledType === 'reply') {
    return 'Scheduled: After Contact Response to the Previous Message';
  }
  if (scheduledType === 'immediate') {
    return `Scheduled: Immediately${index > 0 ? ' after the previous message' : ''}`;
  }
  if (scheduledType === 'delay') {
    if (!delay || delay.length <= 1) {
      return '';
    }
    if (scheduleKind === 'reminder') {
      return `Scheduled: ${delay.replace('-', '')} before appointment`;
    }
    return `Scheduled: ${delay} after previous message`;
  }
  if (scheduledType === 'time' && time) {
    return `Scheduled: ${dayjs(time).format('YYYY-MM-DD hh:mm A')}`;
  }
  return '';
}

export function newDefaultMessage(
  policy: CampaignPolicy,
  config: CampaignConfigSchema,
  position: number,
  /** The message this one is added after; it can decide the schedule. */
  previous?: FormMessage,
): FormMessage {
  const type = config.messageTemplates?.[position]?.messageType === 'mms-only'
    ? 'mms'
    : 'sms';

  const scheduledType = policy.defaultScheduledType(previous);

  return {
    clientKey: newClientKey(),
    type,
    text: '',
    subject: '',
    links: [],
    position,
    scheduledType,
    isSurvey: previous?.isSurvey,
  };
}

export function isMessageDirty(m: FormMessage): boolean {
  if (m.scheduledType === 'delay' && m.delay && m.delay.length < 2) return false;
  let text = '';
  try {
    text = convertHtmlToText(m.text ?? '').trim();
  } catch {
    const element = document.createElement('div');
    element.innerHTML = m.text ?? '';
    text = (element.textContent ?? '').trim();
  }
  return !!text || (m.links?.length ?? 0) > 0;
}

/**
 * Neither of these consults `config.messages.min_limit` / `max_limit`, and that
 * is deliberate: enforcing them would block adding or removing a message on any
 * config that happens to carry them. The message count is therefore not a
 * parameter — nothing about it can change the answer.
 */
export function canAddMessages(policy: CampaignPolicy, config: CampaignConfigSchema): boolean {
  return policy.allowsMultipleMessages(config);
}

export function canRemoveMessage(position: number): boolean {
  return position > 1;
}

/**
 * Which config message template governs a given message index: a single template
 * governs every message; otherwise index-match, with the last template covering
 * all trailing messages.
 */
export function getMessageTemplateForIndex(
  templates: CampaignConfigSchema['messageTemplates'] | undefined,
  index: number,
) {
  return getGoverningMessageTemplate(templates, index);
}
